import React, { useEffect, useState, useCallback } from 'react';
import { apiGet, apiPatch, apiPost } from '../../lib/api-client';

interface UserRecord {
  uid: string;
  email: string | null;
  displayName: string | null;
  role: string;
  status: string;
  sessionsPlayed: number;
  avgScore: number;
  importedHands: number;
  lastActiveAt: number | null;
  createdAt: number;
  notes: string | null;
}

interface PlatformStats {
  totalUsers: number;
  adminUsers: number;
  activeUsersLast7d: number;
  totalSessions: number;
  avgScore: number;
  defaultHands: number;
  userHands: number;
}

const STATUS_COLORS: Record<string, string> = {
  active: '#22c55e',
  suspended: '#f59e0b',
  banned: '#ef4444',
};

function formatDate(ts: number | null): string {
  if (!ts) return 'Never';
  return new Date(ts * 1000).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

function formatDateTime(ts: number | null): string {
  if (!ts) return 'Never';
  return new Date(ts * 1000).toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export function AdminScreen() {
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [sort, setSort] = useState('created_at');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const pageSize = 50;

  // Edit modal
  const [editing, setEditing] = useState<UserRecord | null>(null);
  const [editRole, setEditRole] = useState('');
  const [editStatus, setEditStatus] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editDisplayName, setEditDisplayName] = useState('');
  const [saving, setSaving] = useState(false);

  const loadStats = useCallback(async () => {
    try {
      const data = await apiGet<PlatformStats>('/api/admin/stats');
      setStats(data);
    } catch { /* ignore if not admin yet */ }
  }, []);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = {
        sort, order,
        limit: String(pageSize),
        offset: String(page * pageSize),
      };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (roleFilter) params.role = roleFilter;

      const data = await apiGet<{ total: number; users: UserRecord[] }>('/api/admin/users', params);
      setUsers(data.users);
      setTotal(data.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [sort, order, page, search, statusFilter, roleFilter]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadUsers(); }, [loadUsers]);

  function handleSort(col: string) {
    if (sort === col) {
      setOrder(o => o === 'desc' ? 'asc' : 'desc');
    } else {
      setSort(col);
      setOrder('desc');
    }
    setPage(0);
  }

  function sortIndicator(col: string) {
    if (sort !== col) return '';
    return order === 'desc' ? ' \u25BC' : ' \u25B2';
  }

  function openEdit(user: UserRecord) {
    setEditing(user);
    setEditRole(user.role);
    setEditStatus(user.status);
    setEditNotes(user.notes ?? '');
    setEditDisplayName(user.displayName ?? '');
  }

  async function saveEdit() {
    if (!editing) return;
    setSaving(true);
    try {
      await apiPatch(`/api/admin/users/${editing.uid}`, {
        role: editRole,
        status: editStatus,
        notes: editNotes,
        displayName: editDisplayName,
      });
      setEditing(null);
      loadUsers();
      loadStats();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="admin-screen">
      <h2>Admin Portal</h2>

      {/* Platform Stats */}
      {stats && (
        <div className="admin-stats-grid">
          <div className="admin-stat-card">
            <div className="admin-stat-value">{stats.totalUsers}</div>
            <div className="admin-stat-label">Total Users</div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-value">{stats.activeUsersLast7d}</div>
            <div className="admin-stat-label">Active (7d)</div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-value">{stats.totalSessions.toLocaleString()}</div>
            <div className="admin-stat-label">Total Sessions</div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-value">{stats.avgScore.toFixed(1)}%</div>
            <div className="admin-stat-label">Avg Score</div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-value">{stats.defaultHands.toLocaleString()}</div>
            <div className="admin-stat-label">Default Hands</div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-value">{stats.userHands.toLocaleString()}</div>
            <div className="admin-stat-label">User Hands</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="admin-filters">
        <input
          type="text" placeholder="Search email, name, or UID..."
          value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
          className="admin-search"
        />
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(0); }} className="admin-select">
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="banned">Banned</option>
        </select>
        <select value={roleFilter} onChange={e => { setRoleFilter(e.target.value); setPage(0); }} className="admin-select">
          <option value="">All roles</option>
          <option value="admin">Admin</option>
          <option value="user">User</option>
        </select>
      </div>

      {error && <div className="admin-error">{error}</div>}

      {/* Users Table */}
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('email')}>Email{sortIndicator('email')}</th>
              <th onClick={() => handleSort('display_name')}>Name{sortIndicator('display_name')}</th>
              <th onClick={() => handleSort('role')}>Role{sortIndicator('role')}</th>
              <th onClick={() => handleSort('status')}>Status{sortIndicator('status')}</th>
              <th onClick={() => handleSort('sessions_played')}>Sessions{sortIndicator('sessions_played')}</th>
              <th>Avg Score</th>
              <th onClick={() => handleSort('last_active_at')}>Last Active{sortIndicator('last_active_at')}</th>
              <th onClick={() => handleSort('created_at')}>Joined{sortIndicator('created_at')}</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="admin-loading">Loading...</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={9} className="admin-empty">No users found</td></tr>
            ) : users.map(u => (
              <tr key={u.uid}>
                <td className="admin-email-cell">{u.email ?? u.uid}</td>
                <td>{u.displayName ?? '-'}</td>
                <td>
                  <span className={`admin-role-badge ${u.role}`}>{u.role}</span>
                </td>
                <td>
                  <span className="admin-status-badge" style={{ color: STATUS_COLORS[u.status] }}>
                    {u.status}
                  </span>
                </td>
                <td className="num">{u.sessionsPlayed}</td>
                <td className="num">{u.avgScore > 0 ? `${u.avgScore.toFixed(1)}%` : '-'}</td>
                <td>{formatDate(u.lastActiveAt)}</td>
                <td>{formatDate(u.createdAt)}</td>
                <td>
                  <button className="admin-edit-btn" onClick={() => openEdit(u)}>Edit</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="admin-pagination">
          <button disabled={page === 0} onClick={() => setPage(p => p - 1)}>Previous</button>
          <span>Page {page + 1} of {totalPages}</span>
          <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Next</button>
        </div>
      )}

      {/* Edit Modal */}
      {editing && (
        <div className="admin-modal-backdrop" onClick={() => setEditing(null)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <h3>Edit User</h3>
            <p className="admin-modal-uid">{editing.email ?? editing.uid}</p>

            <label className="admin-field">
              <span>Display Name</span>
              <input type="text" value={editDisplayName} onChange={e => setEditDisplayName(e.target.value)} />
            </label>

            <label className="admin-field">
              <span>Role</span>
              <select value={editRole} onChange={e => setEditRole(e.target.value)}>
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </label>

            <label className="admin-field">
              <span>Status</span>
              <select value={editStatus} onChange={e => setEditStatus(e.target.value)}>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="banned">Banned</option>
              </select>
            </label>

            <label className="admin-field">
              <span>Admin Notes</span>
              <textarea value={editNotes} onChange={e => setEditNotes(e.target.value)} rows={3} />
            </label>

            <div className="admin-modal-stats">
              <span>Sessions: {editing.sessionsPlayed}</span>
              <span>Avg Score: {editing.avgScore > 0 ? `${editing.avgScore.toFixed(1)}%` : 'N/A'}</span>
              <span>Imported: {editing.importedHands} hands</span>
              <span>Joined: {formatDateTime(editing.createdAt)}</span>
              <span>Last active: {formatDateTime(editing.lastActiveAt)}</span>
            </div>

            <div className="admin-modal-actions">
              <button className="admin-btn cancel" onClick={() => setEditing(null)}>Cancel</button>
              <button className="admin-btn save" onClick={saveEdit} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
